// FilesPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  DocumentIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';

// Defina a baseUrl diretamente no componente
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
// const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';

const FileUploadButton = ({ isUploading, onUpload }) => {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
      e.target.value = ''; // reseta para permitir o mesmo arquivo depois
    }
  };
  return (
    <div>
      <Button type="button" disabled={isUploading} onClick={() => inputRef.current?.click()}>
        <ArrowUpTrayIcon className="h-4 w-4" />
        {isUploading ? 'Enviando...' : 'Escolher arquivo'}
      </Button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        disabled={isUploading}
      />
    </div>
  );
};

// Componente Modal de confirmação
const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
      <Card className="w-full max-w-md">
        <CardHeader className="flex-row items-center gap-3 space-y-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
          <CardTitle className="text-lg">Confirmar exclusão</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={onConfirm}>
              Deletar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const FilesPage = () => {
  const [files, setFiles] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData.clienteId || userData._id || userData.id;
  const tokenClient = localStorage.getItem('tokenClient');

  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (tokenClient) {
        params.tokenClient = tokenClient;
      }
      if (clienteId) {
        params.clienteId = clienteId;
      }
      const config = {
        ...getAuthConfig(),
        params
      };
      const response = await axios.get(`${baseUrl}/api/files`, config);
      setFiles(response.data.files || []);
      setError(null);
    } catch (err) {
      setError('Erro ao buscar arquivos');
      console.error('Erro ao buscar arquivos:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, [clienteId, tokenClient]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUploadFile = async (file) => {
    try {
      setIsUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tokenClient', tokenClient);
      formData.append('clienteId', clienteId);
      const authConfig = getAuthConfig();
      const config = {
        ...authConfig,
        headers: {
          ...authConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      };
      const response = await axios.post(
        `${baseUrl}/api/files/upload`,
        formData,
        config
      );
      const uploadedFile = response.data.file || response.data;
      setFiles(prev => [...prev, uploadedFile]);
    } catch (err) {
      console.error('Erro ao enviar arquivo:', err.response?.data || err.message);
      setError('Erro ao enviar arquivo');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const config = {
        ...getAuthConfig(),
        params: {
          tokenClient: tokenClient,
          clienteId: clienteId
        }
      };
      await axios.delete(`${baseUrl}/api/files/${fileId}`, config);
      setFiles(prev => prev.filter(file => file._id !== fileId));
      setShowConfirmation(false);
      // Mostrar mensagem de sucesso
      setError(null); // Limpa qualquer erro anterior
    } catch (err) {
      console.error('Erro ao deletar arquivo:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Erro ao deletar arquivo');
      setShowConfirmation(false);
    }
  };

  const confirmDelete = (fileId) => {
    const file = files.find(f => f._id === fileId);
    const fileName = file?.originalName || file?.name || 'este arquivo';
    setFileToDelete(fileId);
    setShowConfirmation(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimetype) => {
    if (!mimetype) return <DocumentIcon className="h-6 w-6 text-gray-400" />;
    if (mimetype.includes('pdf')) {
      return <DocumentTextIcon className="h-6 w-6 text-red-500" />;
    }
    if (mimetype.includes('word') || mimetype.includes('doc')) {
      return <DocumentTextIcon className="h-6 w-6 text-blue-500" />;
    }
    if (mimetype.includes('spreadsheet') || mimetype.includes('excel')) {
      return <DocumentTextIcon className="h-6 w-6 text-green-500" />;
    }
    return <DocumentIcon className="h-6 w-6 text-gray-400" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Meus Arquivos</h1>
          <p className="text-sm text-muted-foreground">Envie e gerencie seus arquivos.</p>
        </div>
        <FileUploadButton isUploading={isUploadingFile} onUpload={handleUploadFile} />
      </div>

      {error && (
        <Card className="border-destructive/40 bg-destructive/10">
          <CardContent className="flex items-center gap-2 p-4 text-sm text-destructive">
            <XMarkIcon className="h-4 w-4" />
            {error}
          </CardContent>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="border-b border-border bg-muted/30 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Input type="text" placeholder="Pesquisar Arquivos..." className="w-64 pr-10" />
                <svg className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Todos os Tipos</span>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <span className="text-sm text-muted-foreground">Mostrando {files.length} de {files.length} arquivos</span>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-8 text-muted-foreground">
            <div className="flex justify-center items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              Carregando arquivos...
            </div>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            <div className="flex flex-col items-center justify-center">
              <ArrowUpTrayIcon className="h-12 w-12 mb-4 text-muted-foreground" />
              <p className="mb-2">Nenhum arquivo encontrado</p>
              <p className="text-sm">Faça upload de arquivos para começar</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {files.map((file) => {
              const fileId = file._id || file.id || file.key;
              const fileName = file.originalName || file.name || 'Untitled';
              const fileSize = file.bytes || 0;
              const fileType = file.mimetype || 'Unknown';
              const createdAt = file.createdAt ? new Date(file.createdAt) : new Date();

              return (
                <div key={fileId} className="p-4 transition-colors hover:bg-muted/40">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex-shrink-0">{getFileIcon(fileType)}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{fileName}</h3>
                        </div>
                        <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{fileType}</span>
                          <span>{formatFileSize(fileSize)}</span>
                          <span>{createdAt.toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => confirmDelete(fileId)}
                        title="Excluir arquivo"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => handleDeleteFile(fileToDelete)}
        message={`Tem certeza que deseja excluir permanentemente este arquivo? Esta ação não pode ser desfeita.`}
      />
    </div>
  );
};

export default FilesPage;
